"use client";
import React from "react";
import Image from "next/image";
import { BlogProps } from "@/types/blog/blog.type";
import MarkdownRenderer from "./MarkdownRenderer";
import { useLocale } from "@/components/providers/LocaleProvider";
import { pickBlogLocale } from "@/libs/blog.libs";

const formatBlogDate = (raw: string, locale: "en" | "bn") => {
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(locale === "bn" ? "bn-BD" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
};

const BlogDetails = ({ blog }: { blog: BlogProps }) => {
  const { locale, dict } = useLocale();
  const copy = pickBlogLocale(blog, locale);
  return (
    <article className=" space-y-6 px-[5%] md:px-[15%] xl:px-[25%] pb-8">
      {/* Blog Title & Meta */}
      <header className=" flex flex-col gap-y-4">
        <section className=" mb-8">
          <Image
            src={blog.thumbnail}
            alt={`${copy.title} thumbnail`}
            width={600}
            height={200}
            className={` mx-auto  rounded-xl object-cover  max-md:w-full`}
          />
        </section>
        <h1 className="md:text-justify leading-12" style={{ fontSize: "36px" }}>{copy.title}</h1>
        <address>
          <time dateTime={blog.createdAt}>{formatBlogDate(blog.createdAt, locale)}</time>
        </address>

        {/* Medium Link With Icon image path:  /assets/third-party-icons/medium-icon-black.avif */}
        {blog?.links?.medium && (
          <a
            href={blog?.links?.medium}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              src="/assets/third-party-icons/medium-icon-black.avif"
              alt={dict.blogsList.mediumLogoAlt}
              width={24}
              height={24}
            />
          </a>
        )}
      </header>

      {/* v2: render Markdown body when present (locale-aware) */}
      {copy.content ? <MarkdownRenderer source={copy.content} /> : null}

      {/* v1 legacy: render typed-block contents when present and Markdown is missing */}
      {!copy.content && blog.contents?.map((section, index) => {
        const sectionClass = section.className || "";

        switch (section.type) {
          case "header":
            return (
              <h2
                key={index}
                className={`md:text-justify font-semibold text-xl whitespace-pre-line ${sectionClass}`}
              >
                {section.content}
              </h2>
            );

          case "text":
            return (
              <p key={index} className={`md:text-justify whitespace-pre-line ${sectionClass}`}>
                {section.content}
              </p>
            );

          case "image":
            return (
              <figure
                key={index}
                className={`${sectionClass} relative w-full mb-14 sm:w-4/5 mx-auto aspect-[2/1]`}
              >
                <Image
                  src={section.content}
                  alt={copy.title}
                  fill
                  sizes="(max-width: 640px) 100vw, 80vw"
                  className="object-cover"
                />
                <figcaption className="absolute -bottom-10 w-full text-center text-xs text-white ">
                  {section.fig}
                </figcaption>
              </figure>
            );

          case "code":
            return (
              <section
                key={index}
                className={`${sectionClass} my-6 rounded-lg overflow-hidden shadow-lg`}
                aria-label="Code snippet"
              >
                <pre className="bg-dark-gray rounded-xl text-zinc-100 text-sm p-4 overflow-x-auto leading-relaxed">
                  <code>{section.content}</code>
                </pre>
              </section>
            );

          case "list":
            return (
              <ul
                key={index}
                className={`space-y-3 list-disc ml-4 ${sectionClass}`}
              >
                {section.content.split("\n").map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            );

          case "node":
            return (
              <section key={index} className={sectionClass}>
                {section.content}
              </section>
            );

          default:
            return null;
        }
      })}
    </article>
  );
};

export default BlogDetails;
